<script lang="ts">
    import Fa from 'svelte-fa'
    import { faCheck, faCopy, faRepeat, faUsers, faCaretDown, faCaretRight, faHistory, faStickyNote, faCalendarDay } from '@fortawesome/free-solid-svg-icons'
    import dayjs from 'dayjs'
    import 'dayjs/locale/pt-br'
    import { crossfade } from 'svelte/transition'
    import { flip } from 'svelte/animate'
    import { cubicOut } from 'svelte/easing'
    import { createEventDispatcher } from 'svelte'
    import ScheduleColumnHeader from '../schedule-column-header.svelte'
    import NotesComposer from './notes-composer.svelte'
    import { agendaNotes, dayKey, isDone, isMine, isTask, notesOfDay, notesPrefs, toggleDone, toggleNotesPref, type AgendaNote } from './notes-store'

    export let date: Date
    // semanal: uma coluna por dia, com o cabeçalho de dia da semana (igual às colunas de agenda)
    export let weekly: boolean = false
    export let fullWidth: boolean = false
    // desktop, diário: a coluna fica presa à esquerda e no topo da grade, com rolagem própria, para as notas
    // continuarem à vista enquanto se rola o dia ou as agendas para o lado. height = altura visível da grade.
    export let pinned: boolean = false
    export let height: number = 0
    // quando o campo de rodapé aparece:
    // 'always' = sempre (diário); 'hover' = só com o mouse sobre a coluna (semanal, desktop);
    // 'external' = quem decide é o contêiner via composerActive (semanal, mobile: a coluna mais à esquerda).
    // Em qualquer modo, com texto digitado ou item em edição ele não some.
    export let composerMode: 'always' | 'hover' | 'external' = 'always'
    export let composerActive: boolean = false
    // userId → nome, para o "por Fulano" das públicas
    export let authors: { [userId: string]: string } = {}

    const dispatch = createEventDispatcher()

    $: day = dayKey(date)
    $: notes = notesOfDay($agendaNotes, date)
    $: isToday = dayjs(date).isSame(new Date(), 'day')

    // o cabeçalho é o mesmo das agendas: com label = coluna de recurso; sem label = coluna de dia
    $: headerSchedule = { label: weekly ? '' : 'Minhas notas', selectedDate: date } as any
    $: summary = [
        plural(notes.pending.length, 'pendente', 'pendentes'),
        notes.done.length ? plural(notes.done.length, 'concluída', 'concluídas') : '',
        notes.reminders.length ? plural(notes.reminders.length, 'lembrete', 'lembretes') : '',
    ]
        .filter(Boolean)
        .join(' · ')

    // abertas/recolhidas: preferência do usuário, igual em todas as colunas (ver notesPrefs)
    $: showForgotten = $notesPrefs.showForgotten
    $: showShared = $notesPrefs.showShared
    $: showDone = $notesPrefs.showDone
    $: showNotes = $notesPrefs.showNotes

    // copiar é o que faz a nota valer no balcão: o texto vai inteiro (título e conteúdo) para o
    // WhatsApp, para o campo de senha, para onde for
    let copied: string = null
    let copiedTimer = null
    function copyNote(note: AgendaNote) {
        const texto = [note.title, note.details].filter(Boolean).join('\n')
        window.api.utils.utils.copyToClipboard(texto)
        copied = note.agendaNoteId
        if (copiedTimer) clearTimeout(copiedTimer)
        copiedTimer = setTimeout(() => (copied = null), 1500)
    }

    // a edição acontece no campo de rodapé: tocar no item carrega ele lá
    let editing: AgendaNote = null
    let hover = false
    let composerDirty = false
    $: dispatch('dirty', composerDirty)
    $: showComposer = composerMode === 'always' || composerDirty || (composerMode === 'hover' ? hover : composerActive)
    function openEdit(note: AgendaNote) {
        // item público de outra pessoa: só a autora edita
        if (!isMine(note)) return
        // tocar de novo no mesmo item fecha a edição — é o caminho de volta mais natural, já que foi
        // o toque nele que abriu
        editing = editing?.agendaNoteId === note.agendaNoteId ? null : note
    }

    // item criado/salvo pelo campo: ganha o mesmo destaque de chegada da conclusão
    function onSaved(ev: CustomEvent<string>) {
        moving++
        arrived = ev.detail
        setTimeout(() => moving--, MOVE_MS + 80)
        setTimeout(() => {
            if (arrived === ev.detail) arrived = null
        }, MOVE_MS + 1400)
    }

    function plural(qtd: number, singular: string, pluralLabel: string) {
        return qtd + ' ' + (qtd === 1 ? singular : pluralLabel)
    }

    function doneAt(note: AgendaNote, key: string) {
        return dayjs(note.completions[key]).locale('pt-br').format('D [de] MMMM [de] YYYY [às] HH:mm')
    }

    // ---- transição ao concluir / desfazer ----------------------------------------------------------
    // Trocar a tarefa de lista na hora confunde: ela some de baixo do dedo e a seguinte ocupa o lugar,
    // levando a um segundo toque na tarefa errada. Por isso são três tempos:
    // 1) o check (ou o desfazer) aparece no lugar por SETTLE_MS, e novos toques naquela tarefa são ignorados;
    // 2) ela voa até a nova posição (crossfade entre as listas) enquanto as outras deslizam (flip);
    // 3) ao chegar, pisca um destaque para o olho encontrar onde ela foi parar.
    // As animações só valem durante esse movimento: ao trocar de dia ou abrir a coluna, a lista entra seca.
    const SETTLE_MS = 380
    const MOVE_MS = 450
    let settling: { [agendaNoteId: string]: 'check' | 'uncheck' } = {}
    let moving = 0
    let arrived: string = null

    const [send, receive] = crossfade({
        duration: MOVE_MS,
        easing: cubicOut,
        // sem par na outra lista (ex.: "Concluídas" recolhida, ou esquecida concluída no dia dela): esmaece
        fallback: () => ({ duration: 250, easing: cubicOut, css: (t) => `opacity:${t};transform:scale(${0.96 + 0.04 * t})` }),
    })
    const sendMove = (node: Element, params: { key: string }) => (moving ? send(node, params) : { duration: 0 })
    const receiveMove = (node: Element, params: { key: string }) => (moving ? receive(node, params) : { duration: 0 })
    const flipMove = (node: Element, fromTo: { from: DOMRect; to: DOMRect }, params: any = {}) =>
        moving ? flip(node, fromTo, { duration: MOVE_MS, easing: cubicOut, ...params }) : { duration: 0 }

    function settle(note: AgendaNote, key: string, intent: 'check' | 'uncheck') {
        if (settling[note.agendaNoteId]) return
        settling = { ...settling, [note.agendaNoteId]: intent }
        setTimeout(() => {
            moving++
            toggleDone(note.agendaNoteId, key)
            const { [note.agendaNoteId]: _, ...rest } = settling
            settling = rest
            arrived = note.agendaNoteId
            setTimeout(() => moving--, MOVE_MS + 80)
            setTimeout(() => {
                if (arrived === note.agendaNoteId) arrived = null
            }, MOVE_MS + 1400)
        }, SETTLE_MS)
    }

    // estado exibido do check: durante o SETTLE_MS já mostra o resultado do toque
    const shownDone = (note: AgendaNote, key: string, s: typeof settling) => (s[note.agendaNoteId] ? s[note.agendaNoteId] === 'check' : isDone(note, key))
</script>

<!-- o hover só decide se o campo de rodapé aparece (semanal, desktop); não é uma interação por si -->
<!-- svelte-ignore a11y-no-static-element-interactions -->
<div
    class="list schedule-col notes-col scroll-dark"
    class:full-width={fullWidth}
    class:pinned
    style={pinned && height ? `height:${height}px` : ''}
    on:mouseenter={() => (hover = true)}
    on:mouseleave={() => (hover = false)}
>
    <ScheduleColumnHeader schedule={headerSchedule} today={weekly && isToday} subtitleOverride={summary} />
    <div class="notes-body">
        {#if notes.forgotten.length || notes.shared.length}
            <div class="notes-pills">
                {#if notes.forgotten.length}
                    <button type="button" class="notes-pill -forgotten" class:active={showForgotten} on:click={() => toggleNotesPref('showForgotten')}>
                        <Fa icon={faHistory} />
                        <span class="-txt">{plural(notes.forgotten.length, 'tarefa esquecida', 'tarefas esquecidas')}</span>
                    </button>
                {/if}
                {#if notes.shared.length}
                    <button type="button" class="notes-pill -shared" class:active={showShared} on:click={() => toggleNotesPref('showShared')}>
                        <Fa icon={faUsers} />
                        <span class="-txt">{plural(notes.shared.length, 'pública', 'públicas')}</span>
                    </button>
                {/if}
            </div>
        {/if}

        {#if showForgotten && notes.forgotten.length}
            <div class="notes-group -forgotten">
                <div class="notes-group_title">Esquecidas</div>
                <!-- concluir uma esquecida vale para o dia dela, que é onde ela ficou pendente -->
                {#each notes.forgotten as note (note.agendaNoteId)}
                    <div
                        class="note"
                        class:-done={shownDone(note, note.date, settling)}
                        class:-settling={settling[note.agendaNoteId]}
                        out:sendMove|global={{ key: note.agendaNoteId }}
                        animate:flipMove
                    >
                        <button
                            type="button"
                            class="note-check"
                            class:checked={shownDone(note, note.date, settling)}
                            title="Concluir"
                            on:click={() => settle(note, note.date, 'check')}
                        >
                            {#if shownDone(note, note.date, settling)}<Fa icon={faCheck} />{/if}
                        </button>
                        <button type="button" class="note-text" on:click={() => openEdit(note)}>
                            <span class="note-title">{note.title}</span>
                            <span class="note-sub">de {dayjs(note.date).format('DD/MM')}</span>
                        </button>
                    </div>
                {/each}
            </div>
        {/if}

        {#if showShared && notes.shared.length}
            <div class="notes-group -shared">
                <div class="notes-group_title">Públicas</div>
                {#each notes.shared as note (note.agendaNoteId)}
                    <div class="note" class:-done={isTask(note) && shownDone(note, day, settling)} class:-settling={settling[note.agendaNoteId]}>
                        {#if isTask(note)}
                            <button
                                type="button"
                                class="note-check"
                                class:checked={shownDone(note, day, settling)}
                                title={isDone(note, day) ? 'Desfazer' : 'Concluir'}
                                on:click={() => settle(note, day, isDone(note, day) ? 'uncheck' : 'check')}
                            >
                                {#if shownDone(note, day, settling)}<Fa icon={faCheck} />{/if}
                            </button>
                        {:else}
                            <span class="note-icon" title="Lembrete"><Fa icon={faCalendarDay} /></span>
                        {/if}
                        <div class="note-text -readonly">
                            <span class="note-title">
                                {note.title}
                                {#if note.everyDay}<span class="note-flag" title="Todos os dias"><Fa icon={faRepeat} /></span>{/if}
                            </span>
                            {#if note.details}<span class="note-details">{note.details}</span>{/if}
                            <span class="note-sub">por {authors[note.userId] || 'outra pessoa'}</span>
                        </div>
                    </div>
                {/each}
            </div>
        {/if}

        <!-- notas (lembretes) primeiro: não têm conclusão, então ficam fora do vai e vem das tarefas -->
        {#if notes.reminders.length}
            <div class="notes-list">
                {#each notes.reminders as note (note.agendaNoteId)}
                    <div class="note -reminder" class:-arrived={arrived === note.agendaNoteId} class:-editing={editing?.agendaNoteId === note.agendaNoteId}>
                        <span class="note-icon" title="Lembrete"><Fa icon={faCalendarDay} /></span>
                        <button type="button" class="note-text" on:click={() => openEdit(note)}>
                            <span class="note-title">
                                {note.title}
                                {#if note.everyDay}<span class="note-flag" title="Todos os dias"><Fa icon={faRepeat} /></span>{/if}
                                {#if note.isPublic}<span class="note-flag" title="Pública"><Fa icon={faUsers} /></span>{/if}
                            </span>
                            {#if note.details}<span class="note-details">{note.details}</span>{/if}
                        </button>
                    </div>
                {/each}
            </div>
        {/if}

        <div class="notes-list">
            {#each notes.pending as note (note.agendaNoteId)}
                <div
                    class="note"
                    class:-done={shownDone(note, day, settling)}
                    class:-settling={settling[note.agendaNoteId]}
                    class:-arrived={arrived === note.agendaNoteId}
                    class:-editing={editing?.agendaNoteId === note.agendaNoteId}
                    in:receiveMove|global={{ key: note.agendaNoteId }}
                    out:sendMove|global={{ key: note.agendaNoteId }}
                    animate:flipMove
                >
                    <button
                        type="button"
                        class="note-check"
                        class:checked={shownDone(note, day, settling)}
                        title="Concluir"
                        on:click={() => settle(note, day, 'check')}
                    >
                        {#if shownDone(note, day, settling)}<Fa icon={faCheck} />{/if}
                    </button>
                    <button type="button" class="note-text" on:click={() => openEdit(note)}>
                        <span class="note-title">
                            {note.title}
                            {#if note.everyDay}<span class="note-flag" title="Todos os dias"><Fa icon={faRepeat} /></span>{/if}
                            {#if note.isPublic}<span class="note-flag" title="Pública"><Fa icon={faUsers} /></span>{/if}
                        </span>
                        {#if note.details}<span class="note-details">{note.details}</span>{/if}
                    </button>
                </div>
            {/each}
        </div>

        {#if notes.done.length}
            <!-- com a seção recolhida não há para onde voar: o contador pisca no lugar -->
            <button
                type="button"
                class="notes-done-toggle"
                class:-arrived={!showDone && !!arrived && notes.done.some((el) => el.agendaNoteId === arrived)}
                on:click={() => toggleNotesPref('showDone')}
            >
                <Fa icon={showDone ? faCaretDown : faCaretRight} />
                Concluídas ({notes.done.length})
            </button>
            {#if showDone}
                <div class="notes-list">
                    {#each notes.done as note (note.agendaNoteId)}
                        <div
                            class="note"
                            class:-done={shownDone(note, day, settling)}
                            class:-settling={settling[note.agendaNoteId]}
                            class:-arrived={arrived === note.agendaNoteId}
                            class:-editing={editing?.agendaNoteId === note.agendaNoteId}
                            in:receiveMove|global={{ key: note.agendaNoteId }}
                            out:sendMove|global={{ key: note.agendaNoteId }}
                            animate:flipMove
                        >
                            <button
                                type="button"
                                class="note-check"
                                class:checked={shownDone(note, day, settling)}
                                title="Desfazer"
                                on:click={() => settle(note, day, 'uncheck')}
                            >
                                {#if shownDone(note, day, settling)}<Fa icon={faCheck} />{/if}
                            </button>
                            <button type="button" class="note-text" on:click={() => openEdit(note)}>
                                <span class="note-title">{note.title}</span>
                                <span class="note-sub">Concluída em: {doneAt(note, day)}</span>
                            </button>
                        </div>
                    {/each}
                </div>
            {/if}
        {/if}

        <!-- O bloco de anotações: não pertence a dia nenhum, então fica no fim e recolhido. No semanal
             seria a mesma lista repetida sete vezes, por isso só aparece na visão do dia. -->
        {#if !weekly && notes.notes.length}
            <button type="button" class="notes-done-toggle" on:click={() => toggleNotesPref('showNotes')}>
                <Fa icon={showNotes ? faCaretDown : faCaretRight} />
                Notas ({notes.notes.length})
            </button>
            {#if showNotes}
                <div class="notes-list">
                    {#each notes.notes as note (note.agendaNoteId)}
                        <div class="note -block" class:-editing={editing?.agendaNoteId === note.agendaNoteId}>
                            <span class="note-icon" title="Nota"><Fa icon={faStickyNote} /></span>
                            <button type="button" class="note-text" class:-readonly={!isMine(note)} on:click={() => openEdit(note)}>
                                <span class="note-title">
                                    {note.title}
                                    {#if note.isPublic}<span class="note-flag" title="Da equipe"><Fa icon={faUsers} /></span>{/if}
                                </span>
                                {#if note.details}<span class="note-details -full">{note.details}</span>{/if}
                                {#if !isMine(note)}<span class="note-sub">por {authors[note.userId] || 'outra pessoa'}</span>{/if}
                            </button>
                            <button
                                type="button"
                                class="note-copy"
                                class:copied={copied === note.agendaNoteId}
                                title="Copiar"
                                on:click|stopPropagation={() => copyNote(note)}
                            >
                                <Fa icon={copied === note.agendaNoteId ? faCheck : faCopy} />
                            </button>
                        </div>
                    {/each}
                </div>
            {/if}
        {/if}
    </div>
    {#if showComposer}
        <NotesComposer {date} {editing} bind:dirty={composerDirty} on:cancel-edit={() => (editing = null)} on:saved={onSaved} />
    {/if}
</div>

<style>
    /* mesmas larguras da coluna de agenda (schedule-by-column.svelte) */
    .notes-col {
        display: flex;
        flex-direction: column;
        flex: 1 1 350px;
        max-width: 350px;
        min-width: 173px;
        position: relative;
        border-right: 1px solid #202020;
        scrollbar-gutter: initial;
    }
    /* Rolando junto com a grade (mobile e semanal), a coluna cresce com o conteúdo e tem no mínimo a altura
       da área: esticada (align stretch) ela ficava só com a altura visível, e o cabeçalho e o campo de rodapé,
       que são sticky dentro dela, sumiam junto com a caixa ao rolar uma lista longa. A presa (pinned) tem
       altura própria e rolagem interna. */
    .notes-col:not(.pinned) {
        align-self: flex-start;
        min-height: 100%;
    }
    /* ao lado das agendas (desktop, diário): fundo próprio e divisor mais marcado que o das agendas,
       separando o tipo de recurso. No semanal e no mobile a coluna não tem vizinha de outro tipo. */
    .notes-col.pinned {
        background-color: #353535;
        border-right: 3px solid #242424;
        position: sticky;
        top: 0px;
        left: 0px;
        /* acima das colunas que passam por baixo ao rolar para o lado (cabeçalhos são 4) */
        z-index: 5;
        align-self: flex-start;
        overflow-y: auto;
        overflow-x: hidden;
    }
    .notes-body {
        flex-grow: 1;
        /* O campo de rodapé é preso (sticky) e, no celular, ainda sobe para ficar acima da barra
           inferior do app (--notes-composer-bottom, ver schedules-container). Esse quanto que ele
           sobe é espaço que ele passa a cobrir: sem somar aqui, os últimos itens da lista ficam
           embaixo dele. Digitando, a variável volta a zero — a área já termina no topo do teclado. */
        padding: 8px 8px calc(28px + var(--notes-composer-bottom, 0px)) 8px;
        font-size: 14px;
    }

    button {
        font: inherit;
        color: inherit;
        background: none;
        border: 0px;
        padding: 0px;
        text-align: left;
    }
    /* o css global pinta todo button de claro e escurece enquanto pressionado — no item isso virava
       um lampejo cinza no fundo a cada toque. A regra global (button:not(:disabled):active) é mais
       específica que o reset acima, então o :active precisa vir explícito, e ancorado na coluna
       para não apagar o fundo próprio dos botões que têm um (copiar, check marcado). */
    .notes-col button {
        -webkit-tap-highlight-color: transparent;
    }
    .notes-col .note-text:active,
    .notes-col .notes-done-toggle:active,
    .notes-col .notes-pill:active,
    .notes-col .note-check:not(.checked):active {
        background-color: transparent;
    }

    /* ---- badges de esquecidas / públicas (funcionam como toggle) ---- */
    .notes-pills {
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
        margin: 2px 0px 8px 0px;
    }
    .notes-pill {
        display: inline-flex;
        align-items: center;
        gap: 5px;
        font-size: 12px;
        font-weight: 600;
        line-height: 1.3;
        padding: 4px 10px;
        border-radius: 999px;
        cursor: pointer;
        border: 1px solid transparent;
    }
    .notes-pill .-txt {
        position: relative;
        top: 1px;
    }
    .notes-pill.-forgotten {
        background: #e0a83a26;
        color: #f0c060;
    }
    .notes-pill.-forgotten.active {
        border-color: #f0c06099;
    }
    .notes-pill.-shared {
        background: #5b8def26;
        color: #8fb0f5;
    }
    .notes-pill.-shared.active {
        border-color: #8fb0f599;
    }

    .notes-group {
        border-radius: 8px;
        padding: 6px 4px 2px 4px;
        margin-bottom: 8px;
    }
    .notes-group.-forgotten {
        background: #e0a83a14;
    }
    .notes-group.-shared {
        background: #5b8def14;
    }
    .notes-group_title {
        font-size: 11px;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        opacity: 0.6;
        padding: 0px 6px 2px 6px;
    }

    /* ---- nota ---- */
    .note {
        display: flex;
        align-items: flex-start;
        gap: 10px;
        padding: 7px 6px;
        border-radius: 6px;
    }
    .note:hover {
        background: #ffffff0a;
    }
    .note-check {
        flex: 0 0 20px;
        width: 20px;
        height: 20px;
        margin-top: 1px;
        border-radius: 50%;
        border: 2px solid #9a9a9a;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 11px;
        cursor: pointer;
    }
    .note-check:hover {
        border-color: #2ecc71;
    }
    .note-check.checked {
        border-color: #2e9c6d;
        background: #2e9c6d;
        color: #fff;
    }
    .note-text {
        flex: 1 1 10px;
        min-width: 0;
        display: flex;
        flex-direction: column;
        cursor: pointer;
        /* Nunito: sem isso a primeira linha fica ~2px acima do centro do círculo/ícone (medido no navegador) */
        padding-top: 2px;
    }
    .note-text.-readonly {
        cursor: default;
    }
    .note-title {
        line-height: 1.35;
        overflow-wrap: anywhere;
    }
    .note-flag {
        font-size: 10px;
        opacity: 0.55;
        margin-left: 4px;
    }
    .note-details {
        font-size: 12px;
        color: #a8a8a8;
        line-height: 1.35;
        margin-top: 1px;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
    }
    .note-sub {
        font-size: 12px;
        color: #8f8f8f;
        margin-top: 1px;
    }
    .note.-done .note-title {
        text-decoration: line-through;
        opacity: 0.6;
    }

    /* nota (lembrete): ícone no lugar do check, alinhado com ele */
    /* o conteúdo da nota é para ser lido inteiro: ela é o texto que se copia */
    .note-details.-full {
        -webkit-line-clamp: unset;
        display: block;
        white-space: pre-line;
    }
    .note-copy {
        flex: 0 0 auto;
        align-self: flex-start;
        width: 28px;
        height: 28px;
        border-radius: 8px;
        background: #ffffff0f;
        color: inherit;
        opacity: 0.7;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 12px;
    }
    .note-copy:hover {
        opacity: 1;
    }
    .note-copy.copied {
        color: #2ecc71;
        opacity: 1;
    }
    :global(.theme_light) .note-copy {
        background: #eceef2;
    }

    .note-icon {
        flex: 0 0 20px;
        height: 20px;
        margin-top: 1px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 14px;
        color: #e0b040;
    }

    /* ---- transição de concluir / desfazer (ver settle) ---- */
    .note-title {
        transition: opacity 0.2s ease;
    }
    /* 1) o check "estala" no lugar antes de a tarefa sair */
    .note.-settling .note-check {
        animation: note-check-pop 0.32s cubic-bezier(0.34, 1.56, 0.64, 1);
    }
    @keyframes note-check-pop {
        0% {
            transform: scale(0.7);
        }
        100% {
            transform: scale(1);
        }
    }
    /* 3) destaque de chegada, que se apaga sozinho */
    .note.-arrived,
    .notes-done-toggle.-arrived {
        animation: note-arrived 1.4s ease-out;
    }
    @keyframes note-arrived {
        0%,
        25% {
            background-color: #2ecc7138;
        }
        100% {
            background-color: transparent;
        }
    }

    /* item carregado no campo de rodapé para edição */
    .note.-editing {
        background: #2ecc7114;
        box-shadow: inset 3px 0px 0px #2ecc71;
    }

    /* ---- concluídas ---- */
    .notes-done-toggle {
        display: flex;
        align-items: center;
        gap: 8px;
        width: 100%;
        padding: 12px 6px 4px 8px;
        margin-top: 6px;
        border-top: 1px solid #ffffff14;
        font-weight: 600;
        opacity: 0.8;
        cursor: pointer;
    }

    /* ---- tema claro ---- */
    :global(.theme_light .schedulers) .notes-col.pinned {
        background-color: #fafbfb;
        border-right: 3px solid #e8eaed !important;
    }
    :global(.theme_light) .note:hover {
        background: #0000000a;
    }
    :global(.theme_light) .note-check {
        border-color: #9aa0a6;
    }
    :global(.theme_light) .note-check.checked {
        border-color: #1b7a52;
        background: #1b7a52;
    }
    :global(.theme_light) .note-details,
    :global(.theme_light) .note-sub {
        color: #80868b;
    }
    :global(.theme_light) .note-icon {
        color: #c48a0c;
    }
    :global(.theme_light) .note.-editing {
        background: #1b7a5214;
        box-shadow: inset 3px 0px 0px #1b7a52;
    }
    :global(.theme_light) .notes-done-toggle {
        border-top-color: #e8eaed;
    }
    :global(.theme_light) .notes-pill.-forgotten {
        background: #fdf3dd;
        color: #9a6a06;
    }
    :global(.theme_light) .notes-pill.-forgotten.active {
        border-color: #e2a52f;
    }
    :global(.theme_light) .notes-pill.-shared {
        background: #e9edfd;
        color: #3d55c9;
    }
    :global(.theme_light) .notes-pill.-shared.active {
        border-color: #7389f3;
    }
    :global(.theme_light) .notes-group.-forgotten {
        background: #fdf3dd80;
    }
    :global(.theme_light) .notes-group.-shared {
        background: #e9edfd80;
    }

    @media (max-width: 900px) {
        .notes-col {
            min-width: 340px;
        }
        .notes-col.full-width {
            max-width: unset;
            border-right: 0px;
        }
        /* no toque não há hover: o alvo de toque do círculo fica maior sem mudar o desenho */
        .note {
            padding: 9px 6px;
        }
    }

    @media print {
        .notes-col {
            display: none;
        }
    }
</style>
